import { DialogProps, Typography } from '@mui/material'
import { C1Button, C1LoadingButton } from 'c1ui'
import { C1Dialog, C1DialogTitle, C1DialogContent, C1DialogActions } from 'components/core/dialog/C1Dialog'
import { APIError } from 'components/core/errors/api'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { BulkActionArgs, BulkActionCreateRequest, BulkActionGetRequest } from 'pbts/c1/api/bulkaction/v1/bulkaction'
import { FormattedMessage } from 'react-intl'
import { createBulkActionApi, getBulkActionApi } from 'redux_toolkit/bulk-actions'

type BulkActionDialogProps<T> = {
    title?: React.ReactNode
    description?: React.ReactNode
    submitButtonText?: React.ReactNode
    children?: React.ReactNode
    args: BulkActionArgs['typ']
    size?: DialogProps['maxWidth']
} & Omit<BulkActionWrapperDialogProps<T, object>, 'extraProps'>

export type BulkActionWrapperDialogProps<T, E = object> = {
    selections: T[]
    setSelections?: (apps: T[]) => void
    triggerFetchActionStatus?: ReturnType<typeof getBulkActionApi.useLazyGetBulkAction>['trigger']
    extraProps?: E
} & ClosableProps

export const BulkActionDialog = <T, >({
    title,
    description,
    submitButtonText,
    children,
    close,
    triggerFetchActionStatus,
    args,
    selections,
    setSelections,
    size = 'xs',
}: BulkActionDialogProps<T>): React.ReactNode => {
    const { mutate, error, inProgress } = createBulkActionApi()

    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault()
        try {
            const { data } = await mutate(BulkActionCreateRequest.create({ args: { typ: args } }))
            triggerFetchActionStatus?.(BulkActionGetRequest.create({ id: data?.id }), { notify: true, force: true })
            close()
            setSelections?.([])
        } catch (err) {
            // do nothing
        }
    }

    const count = selections.length

    return (
        <C1Dialog open onClose={!inProgress ? close : undefined} fullWidth maxWidth={size}>
            <form onSubmit={handleSubmit}>
                <C1DialogTitle>
                    {title || <FormattedMessage
                        id="bulk_action_dialog_title"
                        defaultMessage="Perform bulk action on {count} {count, plural, one {item} other {items}}"
                        values={{ count }}
                    />}
                </C1DialogTitle>
                <C1DialogContent>
                    <APIError
                        title={<FormattedMessage id="bulk_selected_set_app_owners_action_error" defaultMessage="Error performing bulk action" />}
                        err={error}
                        sx={{ mb: 2 }}
                    />
                    {description && <Typography mb={2}>{description}</Typography>}
                    {children}
                </C1DialogContent>
                <C1DialogActions>
                    <C1Button onClick={!inProgress ? close : undefined} variant="outlined" disabled={inProgress}>
                        <FormattedMessage id="bulk_action_cancel" defaultMessage="Cancel" />
                    </C1Button>
                    <C1LoadingButton type="submit" loading={inProgress} variant="contained">
                        {submitButtonText || <FormattedMessage id="bulk_action_submit" defaultMessage="Submit" />}
                    </C1LoadingButton>
                </C1DialogActions>
            </form>
        </C1Dialog>
    )
}
